import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, GraduationCap, MessageSquare, IndianRupee, Heart, Star, Bell, Loader2, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ItemCard, Stars } from "./MktShared";
import { PageHeader, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

export default function MyAccount() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "" });
    api.get("/mkt/wishlist").then((r) => setWishlist(r.data.wishlist)).catch(() => setWishlist([]));
    api.get("/mkt/my-reviews").then((r) => setReviews(r.data.reviews)).catch(() => setReviews([]));
    api.get("/notifications").then((r) => setNotifs(r.data.notifications)).catch(() => setNotifs([]));
  }, [user]);

  const saveProfile = async () => {
    setBusy(true);
    try { await api.patch("/mkt/account", profile); toast.success("Profile saved"); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const changePw = async () => {
    if (pw.new_password !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try { await api.post("/mkt/account/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const removeWish = async (wid) => {
    try { await api.delete(`/mkt/wishlist/${wid}`); setWishlist((w) => w.filter((x) => x.wish_id !== wid)); toast.success("Removed from wishlist"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const QUICK = [
    { to: "/mkt/orders", icon: ShoppingBag, t: "My Orders", d: "Track purchases, payments & deliveries", testid: "acc-orders" },
    { to: "/mkt/courses", icon: GraduationCap, t: "My Courses", d: "Start learning — lifetime access", testid: "acc-courses" },
    { to: "/mkt/messages", icon: MessageSquare, t: "My Messages", d: "Chat with freelancers securely", testid: "acc-messages" },
    { to: "/mkt/offers", icon: IndianRupee, t: "Custom Offers", d: "Personalised quotes from freelancers", testid: "acc-offers" },
    { to: "/mkt/orders", icon: Package, t: "My Downloads", d: "Digital product files from your orders", testid: "acc-downloads" },
  ];

  return (
    <div data-testid="my-account-page">
      <PageHeader title="My MILTROO Account" subtitle={user?.email} testid="my-account-title" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        {QUICK.map((c, i) => (
          <motion.div key={c.t} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Link to={c.to} data-testid={c.testid} className="block bg-card border border-border rounded-2xl p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all h-full">
              <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}>
                <c.icon className="h-4 w-4 text-white" />
              </div>
              <p className="font-display font-bold text-sm">{c.t}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{c.d}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="profile">
        <TabsList data-testid="account-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="acc-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="acc-tab-wishlist">Wishlist ({wishlist?.length ?? "…"})</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="acc-tab-reviews">My Reviews ({reviews?.length ?? "…"})</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="acc-tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="settings" data-testid="acc-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <div className="max-w-md space-y-4 bg-card border border-border rounded-2xl p-6">
            <div className="space-y-1.5"><Label>Full Name</Label><Input data-testid="acc-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="acc-email" value={user?.email || ""} disabled /></div>
            <div className="space-y-1.5"><Label>Mobile Number</Label><Input data-testid="acc-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
            <Button data-testid="acc-save-profile" onClick={saveProfile} disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Profile"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="wishlist" className="mt-6">
          {!wishlist ? <LoadingRows rows={3} /> : wishlist.length === 0 ? (
            <EmptyState title="Wishlist is empty" body="Save services, products and courses you love — find them here." icon={Heart}>
              <Button data-testid="wishlist-browse" onClick={() => navigate("/marketplace")} className="rounded-full font-bold gradient-bg text-white border-0">Browse Marketplace</Button>
            </EmptyState>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {wishlist.map((w, i) => (
                <div key={w.wish_id} className="relative">
                  <ItemCard item={w.item} kind={w.kind} delay={i} />
                  <button data-testid={`wish-remove-${w.wish_id}`} onClick={() => removeWish(w.wish_id)}
                    className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-background/90 border border-border flex items-center justify-center hover:bg-red-500/10 hover:text-red-500 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          {!reviews ? <LoadingRows rows={3} /> : reviews.length === 0 ? (
            <EmptyState title="No reviews yet" body="Complete an order and share your experience." icon={Star} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 max-w-4xl">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`my-review-${r.id}`} className="bg-card border border-border rounded-xl p-5 space-y-2">
                  <div className="flex items-center justify-between"><Stars rating={r.rating} /><span className="text-[10px] text-muted-foreground">{fmtDateTime(r.created_at)}</span></div>
                  <p className="text-sm">{r.text}</p>
                  <p className="text-[11px] text-muted-foreground">{r.kind} · verified purchase</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          {!notifs ? <LoadingRows rows={3} /> : notifs.length === 0 ? (
            <EmptyState title="No notifications" body="Order updates, messages and offers appear here." icon={Bell} />
          ) : (
            <div className="max-w-2xl space-y-2">
              {notifs.map((n) => (
                <div key={n.id} data-testid={`acc-notif-${n.id}`} className={`bg-card border border-border rounded-xl p-4 ${n.read ? "opacity-60" : ""}`}>
                  <p className="font-bold text-sm">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <div className="max-w-md space-y-4 bg-card border border-border rounded-2xl p-6">
            <p className="font-display font-bold">Change Password</p>
            <div className="space-y-1.5"><Label>Current Password</Label><Input data-testid="acc-pw-current" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>New Password</Label><Input data-testid="acc-pw-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Confirm New Password</Label><Input data-testid="acc-pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></div>
            <Button data-testid="acc-pw-submit" onClick={changePw} disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
